import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TabPanel, TabView } from 'primereact/tabview'
import { ACTION_READ_ANY_GRANTS, GRANTS } from '../../auth/grants'
import { useGrants } from '../../auth/useCurrentUser'
import EmptyState from '../../components/ui/EmptyState'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import DeadlineRequestsPanel from '../deadline-requests/components/DeadlineRequestsPanel'
import { DEADLINE_REQUESTS_LIST_KEY } from '../deadline-requests/hooks/useDeadlineRequests'
import { DEADLINE_REQUEST_TAB } from '../deadline-requests/types'
import TaskClosureTab from './components/TaskClosureTab'
import { useApprovalCounts } from './hooks/useApprovalCounts'
import { APPROVALS_TAB, APPROVALS_TABS, type ApprovalsTab } from './types'
import './approvals.css'

/**
 * <b>Onaylarım</b> (131) — onay mercilerinin karar verdiği iki işi tek ekranda toplar:
 *
 * <ol>
 *   <li><b>Hedef Teslim Tarihi Talepleri</b> — "Hedef Teslim Tarihi Güncelleme Talebi" ekranının onay
 *       kuyruğunun aynısı ({@link DeadlineRequestsPanel}, `INBOX`).
 *   <li><b>Görev Kapatma</b> — Onay Bekleniyor ana görevlerin Kapat / Yeniden Aç kuyruğu
 *       ({@link TaskClosureTab}, Toplantı Yönetimi'nin uçları ve bileşenleri).
 * </ol>
 *
 * <p><b>İki katmanlı görünürlük</b> (kullanıcı kararı 2026-11-03): ekranın kapısı kendi yetkisidir
 * ({@code MY_APPROVALS_VIEW} — menü öğesi ve rota kilidi). Sekmeler ise kendi işlemlerinin mevcut
 * kuralına bakar (FR-005/FR-006): talep sekmesi talep ekranının menü kuralıyla
 * ({@code ACTION_READ_ANY_GRANTS}), görev sekmesi Toplantı Yönetimi'ninkiyle
 * ({@code MEETING_MANAGEMENT_VIEW}) — uçlar o kodlarla korunduğu için sekme görünürlüğü onlarla aynı
 * kalmak zorundadır. Görünmeyen sekmenin bileşeni ve sayı sorgusu hiç kurulmaz — uç zaten reddederdi,
 * ama istek gitmesi merkezî bir hata bildirimi doğururdu. Ekran yetkisi olup hiçbir sekmeyi göremeyen
 * kullanıcıya (rol verisi böyle kurulursa) boş bir sekme çubuğu yerine açıklayıcı boş durum gösterilir.
 *
 * <p><b>Sekme adreste</b> (`?tab=requests|tasks`, research D7): yenilemede aynı sekme açılır.
 * Parametre yoksa ya da kullanıcının göremediği bir sekmeyi gösteriyorsa görünür ilk sekme açılır.
 */
export default function ApprovalsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { hasGrant, hasAnyGrant } = useGrants()
  const [searchParams, setSearchParams] = useSearchParams()

  const visible: Record<ApprovalsTab, boolean> = {
    [APPROVALS_TAB.REQUESTS]: hasAnyGrant(...ACTION_READ_ANY_GRANTS),
    [APPROVALS_TAB.TASKS]: hasGrant(GRANTS.MEETING_MANAGEMENT_VIEW),
  }
  const anyVisible = APPROVALS_TABS.some((tab) => visible[tab])
  const firstVisible = APPROVALS_TABS.find((tab) => visible[tab]) ?? APPROVALS_TAB.REQUESTS
  // Adresteki değer BİLİNEN sekmelerden çözülür, `as` ile zorlanmaz: `visible` düz bir nesne olduğu için
  // `?tab=constructor` / `toString` gibi değerler `Object.prototype` üyesini (truthy) bulur, sekme -1
  // indekse düşer ve gövde boş kalırdı (Duo, MR !367).
  const raw = searchParams.get('tab')
  const requested = APPROVALS_TABS.find((candidate) => candidate === raw)
  const tab = requested && visible[requested] ? requested : firstVisible

  const counts = useApprovalCounts(visible)

  /** Sekme değişimi adrese yazılır; `replace` → sekmeler arası geçiş tarayıcı geçmişini doldurmaz. */
  const changeTab = (index: number) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('tab', APPROVALS_TABS[index])
        return next
      },
      { replace: true },
    )

  /** Sayı yüklenmediyse yalnız etiket — yer tutucu metin ya da "0" yazılmaz (yanlış bilgi olurdu). */
  const header = (label: string, count: number | undefined) =>
    count == null ? label : t('approvals.tab.count', { label, count })

  /** Yenile iki sekmenin listesini ve sayılarını birlikte tazeler (kökler sayı sorgularını da kapsar). */
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: DEADLINE_REQUESTS_LIST_KEY }),
      qc.invalidateQueries({ queryKey: ['meeting-management', 'tasks'] }),
    ])

  return (
    <div className="admin-page approvals-page">
      {/* Başlık = menü adı (anayasa: aynı i18n anahtarı). */}
      <PageHeader
        title={t('nav.tutanak.approvals')}
        // Görünür sekme yoksa tazelenecek liste de yoktur — işlevsiz bir buton çizilmez.
        actions={anyVisible ? <RefreshButton onRefresh={refresh} /> : undefined}
      />

      {!anyVisible && (
        <EmptyState
          icon="pi pi-lock"
          title={t('approvals.empty.noTab.title')}
          description={t('approvals.empty.noTab.description')}
        />
      )}

      {/* İndeksler SABİT (0 = talepler, 1 = görevler): görünmeyen sekme diziden çıkarılmaz,
          `visible={false}` ile düşürülür — indeks ↔ sekme eşlemesi yetkiden bağımsız kalır. */}
      {anyVisible && (
        <TabView activeIndex={APPROVALS_TABS.indexOf(tab)} onTabChange={(e) => changeTab(e.index)}>
          <TabPanel
            header={header(t('approvals.tab.requests'), counts.requests)}
            visible={visible[APPROVALS_TAB.REQUESTS]}
          >
            {visible[APPROVALS_TAB.REQUESTS] && (
              <DeadlineRequestsPanel tab={DEADLINE_REQUEST_TAB.INBOX} />
            )}
          </TabPanel>
          <TabPanel
            header={header(t('approvals.tab.tasks'), counts.tasks)}
            visible={visible[APPROVALS_TAB.TASKS]}
          >
            {visible[APPROVALS_TAB.TASKS] && <TaskClosureTab />}
          </TabPanel>
        </TabView>
      )}
    </div>
  )
}
